import { useState, useEffect } from 'react';
import { admin, activity as at } from '../../styles/tokens';
import { supabase } from '../../lib/supabase';
import { formatActivity } from '../../utils/activityFormatter';

interface ActivityEntry {
  id: string;
  activity_type: string;
  description: string;
  logged_by: string | null;
  created_at: string;
  user_name?: string;
}

function formatActivityTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'p' : 'a';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}

function renderDescription(desc: string): React.ReactElement {
  // Bold any name-like prefix before the first verb/action word
  const parts = desc.split(/(?<=\S)\s(?=status|reassigned|created|updated|cancelled|completed|marked|logged|sent|advanced|rescheduled)/i);
  if (parts.length > 1) {
    return <><span style={{ color: at.nameColor, fontWeight: at.nameFontWeight }}>{parts[0]}</span> {parts.slice(1).join(' ')}</>;
  }
  return <>{desc}</>;
}

interface ActivityFeedProps {
  businessId: string | undefined;
}

export function ActivityFeed({ businessId }: ActivityFeedProps) {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);

  useEffect(() => {
    if (!businessId) return;
    supabase
      .from('activity_log')
      .select('id, activity_type, description, logged_by, created_at')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false })
      .limit(5)
      .then(({ data }) => {
        if (data) setEntries(data as ActivityEntry[]);
      });
  }, [businessId]);

  if (entries.length === 0) {
    return (
      <div style={{ padding: '20px 0', textAlign: 'center', color: admin.caption, fontSize: 13 }}>
        No recent activity.
      </div>
    );
  }

  return (
    <div>
      {entries.map(entry => (
        <div key={entry.id} style={{
          display: 'flex', gap: 10, padding: '8px 0',
          borderBottom: `0.5px solid ${admin.border}`,
        }}>
          <span style={{
            fontSize: 12, color: at.timeColor,
            width: at.timeWidth, flexShrink: 0,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {formatActivityTime(entry.created_at)}
          </span>
          <span style={{ fontSize: 12, color: at.textColor, lineHeight: 1.4 }}>
            {renderDescription(formatActivity(entry.activity_type, entry.description).text)}
          </span>
        </div>
      ))}
    </div>
  );
}
